import { Link } from 'react-router-dom';
import { BlogPost } from '@/lib/supabase';
import { Calendar, Clock, ArrowRight } from 'lucide-react';

interface HeroPostProps {
  post: BlogPost;
  size?: 'large' | 'medium'; // 'large' para el principal, 'medium' para los laterales
}

export function HeroPost({ post, size = 'large' }: HeroPostProps) {
  const readTime = Math.ceil((post.content?.split(/\s+/).length || 0) / 200);
  const isLarge = size === 'large';

  return (
    <Link 
      to={`/blog/${post.slug}`}
      className={`group relative overflow-hidden rounded-2xl block h-full ${isLarge ? 'min-h-[400px] md:min-h-[500px]' : 'min-h-[240px]'}`}
    >
      {/* Imagen de Fondo */}
      <div className="absolute inset-0 bg-slate-200">
        {post.image_url ? (
          <img 
            src={post.image_url} 
            alt={post.title} 
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-900" />
        )}
        {/* Gradiente de legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
      </div>

      {/* Contenido */}
      <div className="absolute bottom-0 left-0 w-full p-6 md:p-8 flex flex-col justify-end h-full">
        {post.categories && (
          <span className="inline-block w-fit bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide mb-3 shadow-sm">
            {post.categories.name}
          </span>
        )}
        
        <h2 className={`font-bold text-white leading-tight mb-3 group-hover:text-blue-200 transition-colors ${isLarge ? 'text-2xl md:text-4xl' : 'text-xl'}`}>
          {post.title}
        </h2>

        {isLarge && (
          <p className="text-slate-200 text-base md:text-lg line-clamp-2 mb-4 max-w-2xl">
            {post.excerpt}
          </p>
        )}

        <div className="flex items-center gap-4 text-xs md:text-sm text-slate-300 font-medium">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            {new Date(post.published_at || post.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            {readTime} min
          </div>
          {isLarge && (
            <div className="flex items-center gap-1 text-white ml-auto group-hover:translate-x-1 transition-transform">
              Leer artículo <ArrowRight className="w-4 h-4" />
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
